import { ListingDto, ListingDtoSchema } from '@hermes/domains'
import { ExtendedListing } from '@hermes/types'
import { v4 as uuidv4 } from 'uuid'
import { z } from 'zod'
import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

const StateSchema = z.object({
  userId: z.number(),
  sellerId: z.number(),
  cart: z.object({
    id: z.string(),
    items: z.array(z.object({ uuid: z.string() }))
  }),
  fulfillment: z.object({
    shippingProvider: z.enum(['helthjem', 'dooris', 'postnord', 'none']), // TODO: Get from shared schema
    shippingMethod: z.enum(['shipping', 'meetup']), // TODO: Get from shared schema
    shippingAddressId: z.number(),
    recipient: z.object({
      email: z.string(),
      firstName: z.string(),
      lastName: z.string(),
      fullName: z.string(),
      phone: z.string()
    })
  }),
  payment: z.object({
    checkoutProvider: z.string(),
    royaltyDonationAmount: z.number(),
    promotionCode: z.string().optional().nullable()
  })
})

type State = z.infer<typeof StateSchema>

type Actions = {
  addToCart: (item: ListingDto | ExtendedListing) => void
  removeFromCart: (saleId: (ListingDto | ExtendedListing)['uuid']) => void
  setAddress: (addressId: number) => void
  setRecipientDetail: (recipientDetails: Partial<State['fulfillment']['recipient']>) => void
  setShippingProvider: (provider: State['fulfillment']['shippingProvider']) => void
  setShippingMethod: (method: State['fulfillment']['shippingMethod']) => void
  clearCart: () => void
}

const initialState: State = {
  userId: 0,
  sellerId: 0,
  cart: {
    id: uuidv4(),
    items: []
  },
  fulfillment: {
    shippingProvider: 'helthjem',
    shippingMethod: 'shipping',
    shippingAddressId: 0,
    recipient: {
      email: '',
      firstName: '',
      lastName: '',
      fullName: '',
      phone: ''
    }
  },
  payment: {
    checkoutProvider: '',
    royaltyDonationAmount: 0,
    promotionCode: null
  }
}

const useCheckoutStore = create<State & Actions>()(
  persist(
    (set, get) => ({
      ...initialState,
      getCart: () => get().cart,
      getCartItems: () => get().cart.items,
      addToCart: (rawItem) => {
        set((state) => {
          const itemExists = state.cart.items.some((cartItem) => cartItem.uuid === rawItem.uuid)
          return {
            cart: {
              ...state.cart,
              items: itemExists ? [...state.cart.items] : [...state.cart.items, rawItem]
            }
          }
        })
      },
      removeFromCart: (saleId) =>
        set((state) => ({
          cart: {
            ...state.cart,
            items: state.cart.items.filter((item) => item.uuid !== saleId)
          }
        })),
      setAddress: (addressId: number) =>
        set((state) => ({
          fulfillment: {
            ...state.fulfillment,
            shippingAddressId: addressId
          }
        })),
      setRecipientDetail: (recipientDetails) =>
        set((state) => ({
          fulfillment: {
            ...state.fulfillment,
            recipient: {
              ...state.fulfillment.recipient,
              ...recipientDetails
            }
          }
        })),
      setShippingProvider: (provider) => {
        set((state) => ({
          fulfillment: {
            ...state.fulfillment,
            shippingProvider: provider
          }
        }))
      },
      setShippingMethod: (method) => {
        set((state) => ({
          fulfillment: {
            ...state.fulfillment,
            shippingMethod: method
          }
        }))
      },
      clearCart: () => {
        set(initialState)
      }
    }),
    {
      name: 'cart-store',
      storage: createJSONStorage(() => localStorage)
    }
  )
)

export { useCheckoutStore }
